import type { ICatalogItemDto, ICatalogResponseDto } from '~/dtos/hub/hub.models.generated';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import {
  QUERY_SEARCH_PARAM_ACTION,
  QUERY_SEARCH_PARAM_CATALOG_AGRID,
  QUERY_SEARCH_PARAM_CATALOG_HAGRID,
  QUERY_SEARCH_PARAM_CATALOG_HWGID,
  QUERY_SEARCH_PARAM_NOVELTIES
} from '~/features/catalog/mappers/catalogSearchQueryMapper';
import { CatalogNavigationItem } from '~/features/catalog/components/navigation/CatalogNavigationItem';
import { useBasePath } from '~/common/hooks/useBasePath';
import { LucideBadgeAlert, LucideClock3 } from 'lucide-react';
import { FilterCatalogNavigationItem } from '~/features/catalog/components/navigation/FilterCatalogNavigationItem';
import { TrackNavigationClick } from '~/tracking/TrackNavigationClick';
import { isNavigationLeaf } from '~/features/catalog/utils/categoryNavigationUtil';

export interface CategoryPath {
  hwgId?: number;
  hagrId?: number;
  agrId?: number;
}

export interface SearchCategory {
  id: number;
  name: string;
  path: CategoryPath;
  type: 'hwg' | 'hagr' | 'agr' | null;
}

export interface SelectableItem<T> {
  item: T;
  children: SelectableItem<T>[];
  selected: boolean;
}

export type SelectableSearchCategory = SelectableItem<SearchCategory>;

export function mapLevelToType(level: number) {
  switch (level) {
    case 1:
      return 'hwg';
    case 2:
      return 'hagr';
    case 3:
      return 'agr';
    default:
      return null;
  }
}

export function mapSearchCategoryDto(
  searchCategory: ICatalogItemDto,
  level: number,
  parentPath: CategoryPath
): SelectableSearchCategory {
  const newLevel = level + 1;

  const newPath = { ...parentPath };

  switch (newLevel) {
    case 1:
      newPath.hwgId = searchCategory.id;
      break;
    case 2:
      newPath.hagrId = searchCategory.id;
      break;
    case 3:
      newPath.agrId = searchCategory.id;
      break;
  }
  const type = mapLevelToType(newLevel);

  return {
    item: { name: searchCategory.name, id: searchCategory.id, type, path: newPath },
    selected: false,
    children: searchCategory.child.map(x => mapSearchCategoryDto(x, newLevel, newPath))
  } as SelectableSearchCategory;
}

export function setSelectedCategories(path: CategoryPath, node: SelectableSearchCategory) {
  switch (node.item.type) {
    case 'hwg':
      node.selected = node.item.id === path.hwgId;
      break;
    case 'hagr':
      node.selected = node.item.id === path.hagrId;
      break;
    case 'agr':
      node.selected = node.item.id === path.agrId;
      break;
  }

  for (const child of node.children) {
    setSelectedCategories(path, child);
  }

  return node;
}

const HWG_PALETTE = 12;

type CategoriesNavigationProps = {
  compact?: boolean;
  catalogItems: ICatalogResponseDto;
  onCategorySelected?: (category: SelectableSearchCategory) => void;
  onShowAllInCategorySelected?: () => void;
  onActionSelected?: () => void;
  onNoveltiesSelected?: () => void;
  onSearchCategorySelected?: () => void;
  actionExclude?: boolean;
};

export default function CategoryNavigation({
  catalogItems,
  compact,
  onCategorySelected,
  onShowAllInCategorySelected,
  onActionSelected,
  onNoveltiesSelected,
  onSearchCategorySelected,
  actionExclude
}: Readonly<CategoriesNavigationProps>) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { path } = useBasePath();
  const isOnCatalogPage = location.pathname.endsWith('/catalog');

  const [categories, setCategories] = useState<SelectableSearchCategory[]>(() => {
    return catalogItems.items
      .map(x => mapSearchCategoryDto(x, 0, {}))
      .filter(x => !(x.item.type === 'hwg' && x.item.id === HWG_PALETTE));
  });

  const mapUrlToCategoryPath = (searchParams: URLSearchParams): CategoryPath => {
    const hwgId = searchParams.has(QUERY_SEARCH_PARAM_CATALOG_HWGID)
      ? searchParams.get(QUERY_SEARCH_PARAM_CATALOG_HWGID)
      : undefined;
    const hagrId = searchParams.has(QUERY_SEARCH_PARAM_CATALOG_HAGRID)
      ? searchParams.get(QUERY_SEARCH_PARAM_CATALOG_HAGRID)
      : undefined;
    const agrId = searchParams.has(QUERY_SEARCH_PARAM_CATALOG_AGRID)
      ? searchParams.get(QUERY_SEARCH_PARAM_CATALOG_AGRID)
      : undefined;
    return {
      hwgId: hwgId ? Number.parseInt(hwgId) : undefined,
      hagrId: hagrId ? Number.parseInt(hagrId) : undefined,
      agrId: agrId ? Number.parseInt(agrId) : undefined
    };
  };

  const [categoryPath, setCategoryPath] = useState<CategoryPath>(() => {
    return mapUrlToCategoryPath(searchParams);
  });

  const searchTerm = searchParams.get('searchTerm') ?? '';
  const rootCategory: SelectableSearchCategory = {
    selected: true,
    item: { id: 0, name: t('Gesamtsortiment'), type: null, path: {} },
    children: categories
  };
  const hasSearchTerm = Boolean(searchTerm?.length);

  const navigateToCategory = (cat: SelectableSearchCategory) => {
    const searchParams = new URLSearchParams();
    if (cat.item.path.hwgId) {
      searchParams.set(QUERY_SEARCH_PARAM_CATALOG_HWGID, cat.item.path.hwgId.toString());
    }
    if (cat.item.path.hagrId) {
      searchParams.set(QUERY_SEARCH_PARAM_CATALOG_HAGRID, cat.item.path.hagrId.toString());
    }
    if (cat.item.path.agrId) {
      searchParams.set(QUERY_SEARCH_PARAM_CATALOG_AGRID, cat.item.path.agrId.toString());
    }

    navigate(`${path}/catalog?${searchParams.toString()}`, { replace: isOnCatalogPage });
  };

  const handleCategorySelection = (cat: SelectableSearchCategory) => {
    // Update selected categories immediately -> Optimistic UI, no need to wait for the url to update
    setCategoryPath(cat.item.path);
    onCategorySelected?.(cat);

    if (!compact || isNavigationLeaf(cat)) {
      navigateToCategory(cat);
    }
  };

  const handleShowAllInCategorySelection = (cat: SelectableSearchCategory) => {
    setCategoryPath(cat.item.path);
    onShowAllInCategorySelected?.();
    navigateToCategory(cat);
  };

  const handleActionSelection = () => {
    onActionSelected?.();

    const searchParams = new URLSearchParams();
    searchParams.set(QUERY_SEARCH_PARAM_ACTION, 'true');
    setSearchParams(searchParams);

    navigate(`${path}/catalog?${searchParams.toString()}`, { replace: isOnCatalogPage });
  };

  const handleNoveltiesSelection = () => {
    onNoveltiesSelected?.();

    const searchParams = new URLSearchParams();
    searchParams.set(QUERY_SEARCH_PARAM_NOVELTIES, 'true');
    setSearchParams(searchParams);

    navigate(`${path}/catalog?${searchParams.toString()}`, { replace: isOnCatalogPage });
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCategories(prevCategories => prevCategories.map(x => setSelectedCategories(categoryPath ?? {}, x)));
  }, [categoryPath]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCategoryPath(mapUrlToCategoryPath(searchParams));
  }, [searchParams]);

  return (
    <>
      <h2 className="block mb-4 text-xl font-bold">{t('Sortiment')}</h2>
      <CatalogNavigationItem
        onCategorySelected={handleCategorySelection}
        onShowAllInCategorySelected={handleShowAllInCategorySelection}
        selectableCategory={rootCategory}
        showOnlyRoot={hasSearchTerm}
        compact={compact}
        selectedCategoryPath={categoryPath}
        onSearchCategorySelected={onSearchCategorySelected}
      >
        <>
          {!categoryPath?.hwgId && !hasSearchTerm && !actionExclude && catalogItems.hasActions && (
            <TrackNavigationClick label={t('Aktionen')}>
              <FilterCatalogNavigationItem filterQueryParam={QUERY_SEARCH_PARAM_ACTION} onClick={handleActionSelection}>
                <LucideBadgeAlert className={'text-primary'} />
                <div data-testid={'nav-sale-link'}>{t('Aktionen')}</div>
              </FilterCatalogNavigationItem>
            </TrackNavigationClick>
          )}
          {!categoryPath?.hwgId && !hasSearchTerm && catalogItems.hasNovelties && (
            <TrackNavigationClick label={t('Neuheiten')}>
              <FilterCatalogNavigationItem
                filterQueryParam={QUERY_SEARCH_PARAM_NOVELTIES}
                onClick={handleNoveltiesSelection}
              >
                <LucideClock3 />
                <div data-testid={'nav-novelties-link'}>{t('Neuheiten')}</div>
              </FilterCatalogNavigationItem>
            </TrackNavigationClick>
          )}
        </>
      </CatalogNavigationItem>
    </>
  );
}
